import request from '@/api-v2/request';
import type {
  RegimeAnalysisAttributeDTO,
  RegimeAnalysisAttributeResponseDTO,
  RegimeSignalOptionsResponseDTO,
  RegimeSignalProfileRequestDTO,
  RegimeSignalProfileResponseDTO,
} from './index';
import type { IPortfolioTreeTracksQuery } from '@/types/IPortfolioTreeTracksQuery';
import type { IPortfolioTreeTrackResponse } from '@/types/IPortfolioTreeTracksResponse';
import type { ITrackQuery } from '@/types/ITrackQuery';
import type { StrategyTrackResponseDTO } from '../strategies';

export const getRegimes = (signal: AbortSignal): Promise<RegimeAnalysisAttributeResponseDTO[]> =>
  request({
    url: `/regime-analysis/regime`,
    method: 'GET',
    signal,
  }).then((o): RegimeAnalysisAttributeResponseDTO[] => o.data);

export const postRegime = (payload: RegimeAnalysisAttributeDTO): Promise<RegimeAnalysisAttributeResponseDTO> =>
  request({
    url: `/regime-analysis/regime`,
    method: 'POST',
    data: payload,
  }).then((o): RegimeAnalysisAttributeResponseDTO => o.data);

export const updateRegime = (
  id: string,
  payload: RegimeAnalysisAttributeDTO,
): Promise<RegimeAnalysisAttributeResponseDTO> =>
  request({
    url: `/regime-analysis/regime/${id}`,
    method: 'POST',
    data: payload,
  }).then((o): RegimeAnalysisAttributeResponseDTO => o.data);

export const deleteRegime = (id: string): Promise<RegimeAnalysisAttributeResponseDTO> =>
  request({
    url: `/regime-analysis/regime/${id}`,
    method: 'DELETE',
  }).then((o): RegimeAnalysisAttributeResponseDTO => o.data);

/**
 * Fetch available signal options for regime analysis dropdowns
 * GET /regime-analysis/signal-options
 */
export const getSignalOptions = (signal: AbortSignal): Promise<RegimeSignalOptionsResponseDTO> =>
  request({
    url: `/regime-analysis/signal-options`,
    method: 'GET',
    signal,
  }).then((o): RegimeSignalOptionsResponseDTO => o.data);

/**
 * Fetch signal profile data including raw track and KDE distribution
 * POST /regime-analysis/signal-profile
 */
export const getSignalProfile = (
  payload: RegimeSignalProfileRequestDTO,
  signal: AbortSignal,
): Promise<RegimeSignalProfileResponseDTO> =>
  request({
    url: `/regime-analysis/signal-profile`,
    method: 'POST',
    data: payload,
    signal,
  }).then((o): RegimeSignalProfileResponseDTO => o.data);

export const previewRegimeOnStrategy = ({
  code,
  query,
  attribute,
  signal,
}: {
  code: string;
  query?: ITrackQuery;
  attribute: RegimeAnalysisAttributeDTO;
  signal?: AbortSignal;
}): Promise<StrategyTrackResponseDTO> =>
  request({
    url: `/strategies/${code}/track`,
    method: 'POST',
    data: {
      ...query,
      attribute,
    },
    signal,
  }).then((o): StrategyTrackResponseDTO => o.data);

export const previewRegimeOnPortfolio = (
  slug: string,
  query: IPortfolioTreeTracksQuery,
  attribute: RegimeAnalysisAttributeDTO,
  signal?: AbortSignal,
): Promise<IPortfolioTreeTrackResponse[]> =>
  request({
    url: `/portfolio-trees/${slug}/tracks`,
    method: 'POST',
    data: {
      ...query,
      attribute,
    },
    signal,
  }).then((o): IPortfolioTreeTrackResponse[] => o.data);
